/**
 * Post-pack filters shared by the server packer and the designer packer.
 * Occupancy is snapped to 5 cm so small row-phase jitter does not split a
 * railable block.
 */

export type PackedCellRect = {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
};

export type PackedCell = {
  rect: PackedCellRect;
  orientation: 'PORTRAIT' | 'LANDSCAPE';
};

const occupancySnapM = 0.05;

/** Largest connected group; ties go to the group nearest the eave (lowest v). */
export const largestIsland = <T extends PackedCell>(
  placed: T[],
  gapM: number,
  axisGaps?: { columnGapM: number; rowGapM: number }
): T[] => {
  if (placed.length <= 1) return placed;
  const uSlack = (axisGaps?.columnGapM ?? gapM) + occupancySnapM;
  const vSlack = (axisGaps?.rowGapM ?? gapM) + occupancySnapM;
  const touches = (a: PackedCellRect, b: PackedCellRect): boolean => {
    const uOverlap =
      Math.min(a.u1, b.u1) - Math.max(a.u0, b.u0) > occupancySnapM;
    const vOverlap =
      Math.min(a.v1, b.v1) - Math.max(a.v0, b.v0) > occupancySnapM;
    const uGap = Math.max(a.u0, b.u0) - Math.min(a.u1, b.u1);
    const vGap = Math.max(a.v0, b.v0) - Math.min(a.v1, b.v1);
    return (uOverlap && vGap <= vSlack) || (vOverlap && uGap <= uSlack);
  };
  const seen = Array.from({ length: placed.length }, () => false);
  let best: T[] = [];
  for (let i = 0; i < placed.length; i++) {
    if (seen[i]) continue;
    const group: T[] = [];
    const stack = [i];
    seen[i] = true;
    while (stack.length > 0) {
      const k = stack.pop() as number;
      group.push(placed[k]);
      for (let j = 0; j < placed.length; j++) {
        if (!seen[j] && touches(placed[k].rect, placed[j].rect)) {
          seen[j] = true;
          stack.push(j);
        }
      }
    }
    const lower = (g: T[]) => Math.min(...g.map((p) => p.rect.v0));
    if (
      group.length > best.length ||
      (group.length === best.length && lower(group) < lower(best))
    ) {
      best = group;
    }
  }
  return best;
};

/**
 * Largest filled rectangle of panels (YourPower `quadrilateral_arrays`).
 *
 * Rows on a hip or a centred pack do not share a u0 grid, so an occupancy
 * matrix of snapped origins collapses to a couple of panels. Inscribe a
 * block instead: consecutive rows, overlapping u-span, no gaps.
 */
export const largestFilledRectangle = <T extends PackedCell>(
  placed: T[]
): T[] => {
  if (placed.length <= 1) return placed;
  const round = (x: number) => Math.round(x / occupancySnapM) * occupancySnapM;
  const rowMap = new Map<number, T[]>();
  for (const panel of placed) {
    const key = round(panel.rect.v0);
    const row = rowMap.get(key);
    if (row) row.push(panel);
    else rowMap.set(key, [panel]);
  }
  const rows = [...rowMap.entries()]
    .toSorted((a, b) => a[0] - b[0])
    .map(([, panels]) => panels.toSorted((a, b) => a.rect.u0 - b.rect.u0));
  const contiguous = (row: T[]): boolean => {
    if (row.length <= 1) return true;
    const pitch = row[0].rect.u1 - row[0].rect.u0;
    const slack = pitch + occupancySnapM * 2;
    for (let i = 1; i < row.length; i++) {
      const gap = row[i].rect.u0 - row[i - 1].rect.u1;
      if (gap > slack) return false;
    }
    return true;
  };
  let bestRect: T[] = [];
  for (let r0 = 0; r0 < rows.length; r0++) {
    for (let r1 = r0; r1 < rows.length; r1++) {
      const slice = rows.slice(r0, r1 + 1);
      const left = Math.max(...slice.map((row) => row[0].rect.u0));
      const right = Math.min(
        ...slice.map((row) => row[row.length - 1].rect.u1)
      );
      if (right - left < occupancySnapM) continue;
      const cells: T[] = [];
      let full = true;
      for (const row of slice) {
        const inBand = row.filter(
          (p) =>
            p.rect.u0 >= left - occupancySnapM &&
            p.rect.u1 <= right + occupancySnapM
        );
        if (inBand.length === 0 || !contiguous(inBand)) {
          full = false;
          break;
        }
        cells.push(...inBand);
      }
      if (full && cells.length > bestRect.length) bestRect = cells;
    }
  }
  return bestRect.length > 0 ? bestRect : placed.slice(0, 1);
};
